import { useState, useEffect } from "react";
import { Button, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch } from "../../redux";
import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";

export type AllowedOriginsSettingsFormData = {
  id?: string;
  agentId?: string;
  allowedOrigins?: string[];
};

type AllowedOriginsSettingsFormProps = {
  config: AllowedOriginsSettingsFormData;
  onChange: (key: string) => (value: unknown) => void;
};

export const AllowedOriginsSettingsForm = ({ config, onChange }: AllowedOriginsSettingsFormProps) => {
  const dispatch = useAppDispatch();
  const [newOrigin, setNewOrigin] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [origins, setOrigins] = useState<string[]>(config.allowedOrigins || []);

  useEffect(() => {
    setOrigins(config.allowedOrigins || []);
    setNewOrigin("");
    setIsSaving(false);
  }, [config.id]);

  const hasChanges = JSON.stringify(origins) !== JSON.stringify(config.allowedOrigins || []);

  const addOrigin = () => {
    const trimmed = newOrigin.trim();
    if (!trimmed || origins.includes(trimmed)) return;
    setOrigins([...origins, trimmed]);
    setNewOrigin("");
  };

  const removeOrigin = (index: number) => {
    setOrigins(origins.filter((_, i) => i !== index));
  };

  const onSave = async () => {
    if (!config.id || !config.agentId) return;

    setIsSaving(true);
    try {
      onChange("allowedOrigins")(origins);
      await dispatch(
        agentEnvironmentActions.updateById({
          id: config.id,
          agentId: config.agentId,
          allowedOrigins: origins,
        }),
      );
    } catch (error) {
      console.error("Error saving allowed origins:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addOrigin();
    }
  };

  return (
    <View className="space-y-6">
      <View className="space-y-2">
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          Specify which origins are allowed to call this agent from the browser. Add the full origin including protocol (e.g. https://your-app.com). Use * to
          allow all origins.
        </Text>
      </View>

      <View className="space-y-4">
        <View className="flex items-end gap-2">
          <InputField
            label="Add Origin"
            labelClassName="!w-full"
            className="!w-full flex-1"
            placeholder="https://your-app.com"
            value={newOrigin}
            onChange={setNewOrigin}
            onKeyDown={onKeyDown}
          />
          <Button
            variant="neutral"
            size="sm"
            outline
            onClick={addOrigin}
            disabled={!newOrigin.trim()}
          >
            <Icon
              name="add"
              size={14}
            />
            Add
          </Button>
        </View>

        {origins.length > 0 && (
          <View className="space-y-2">
            {origins.map((origin, index) => (
              <View
                key={index}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
              >
                <Text className="text-sm font-mono text-gray-700 dark:text-gray-300">{origin}</Text>
                <button
                  type="button"
                  onClick={() => removeOrigin(index)}
                  className="flex items-center justify-center p-1 rounded text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <Icon
                    name="close"
                    size={14}
                  />
                </button>
              </View>
            ))}
          </View>
        )}

        {origins.length === 0 && (
          <View className="px-3 py-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
            <Text className="text-sm text-gray-400 dark:text-gray-500 text-center">
              No allowed origins configured. Browser-based calls to this agent will be blocked by CORS.
            </Text>
          </View>
        )}
      </View>

      <View className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="success"
          size="sm"
          outline
          onClick={onSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? (
            <Icon
              name="loader"
              size={14}
              className="animate-spin"
            />
          ) : (
            <Icon
              name="check"
              size={14}
            />
          )}
          Save
        </Button>
      </View>
    </View>
  );
};
